import React, { useState, useEffect } from "react";
import { CheckCircle2, XCircle, LogIn, LogOut, PauseCircle, PlayCircle, Loader2, Wifi, ShieldCheck } from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

async function nfcApi(params) {
  const res = await fetch(`${API_URL}/api/nfc/clock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error('NFC API error');
  return res.json();
}

const TYPE_LABELS = { entry: "Entrada", exit: "Sortida", pause_start: "Inici Pausa", pause_end: "Fi Pausa" };
const TYPE_ICONS = { entry: LogIn, exit: LogOut, pause_start: PauseCircle, pause_end: PlayCircle };
const TYPE_COLORS = {
  entry: "bg-emerald-500 hover:bg-emerald-600",
  exit: "bg-rose-500 hover:bg-rose-600",
  pause_start: "bg-amber-400 hover:bg-amber-500",
  pause_end: "bg-blue-500 hover:bg-blue-600",
};

function getNextAction(records) {
  if (!records || records.length === 0) return "entry";
  const active = records.filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const last = active[active.length - 1];
  if (!last || last.type === "exit") return "entry";
  if (last.type === "entry" || last.type === "pause_end") return "exit";
  if (last.type === "pause_start") return "pause_end";
  return "entry";
}

function getDeviceInfo() {
  return {
    userAgent: navigator.userAgent.substring(0, 120),
    platform: navigator.platform || "unknown",
    language: navigator.language || "unknown",
  };
}

export default function NFCClock() {
  const urlParams = new URLSearchParams(window.location.search);
  const tagId = urlParams.get("tag");
  const venueId = urlParams.get("venue");

  const [step, setStep] = useState("nfc"); // nfc | pin_auth | ready | confirming | success | error
  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [venue, setVenue] = useState(null);
  const [tag, setTag] = useState(null);
  const [todayRecords, setTodayRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [suggestedAction, setSuggestedAction] = useState("entry");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { type, time }
  const [now, setNow] = useState(moment());
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");

  useEffect(() => {
    const t = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Auto-init if tag is in URL (from NFC scan)
    if (tagId && venueId) {
      initWithTag();
    }
  }, []);

  const initWithTag = async () => {
    if (!tagId || !venueId) { setError("Falta tag o venue"); setStep("error"); return; }

    try {
      const res = await nfcApi({ action: 'loadDataByTag', venueId, tagId });
      const data = res.data;

      if (!data.venue) { setError("Local no trobat."); setStep("error"); return; }
      setVenue(data.venue);
      if (data.tag) setTag(data.tag);
      setPin("");
      setPinError("");
      setStep("pin_auth");
    } catch (err) {
      setError("Error carregant dades per NFC.");
      setStep("error");
    }
  };

  const handlePinSubmit = async () => {
    if (!pin || !venue) return;

    // Find member by PIN
    try {
      const res = await nfcApi({ 
        action: 'findMemberByPin', 
        venueId, 
        type: pin
      });
      
      if (!res.data.member) {
        setPinError("PIN incorrecte");
        setPin("");
        return;
      }

      const foundMember = res.data.member;
      setMember(foundMember);
      setUser({ email: foundMember.user_email, full_name: foundMember.user_name });
      
      // Load user records
      const recordsRes = await nfcApi({ 
        action: 'loadData', 
        venueId, 
        userEmail: foundMember.user_email, 
        tagId 
      });
      setTodayRecords(recordsRes.data.todayRecords || []);
      setAllRecords(recordsRes.data.allRecords || []);
      setSuggestedAction(getNextAction(recordsRes.data.todayRecords || []));
      setPinError("");
      setPin("");
      setStep("ready");
    } catch (err) {
      setPinError("Error verificant PIN");
      setPin("");
    }
  };

  const handleAction = async (type) => {
    setSaving(true);
    setStep("confirming");

    try {
      const res = await nfcApi({ 
        action: 'recordAction', 
        venueId, 
        userEmail: user.email, 
        tagId, 
        type 
      });
      const data = res.data;

      if (!data.success) throw new Error("Error registrant fitxatge");

      setSaving(false);
      setResult({ type, time: moment(data.time).format("HH:mm:ss") });
      setStep("success");

      // Recarrega dades i torna a ready després de 2 segons
      setTimeout(async () => {
        try {
          const res = await nfcApi({ action: 'loadData', venueId, userEmail: user.email, tagId });
          const updatedData = res.data;
          setTodayRecords(updatedData.todayRecords || []);
          setSuggestedAction(getNextAction(updatedData.todayRecords || []));
        } catch (err) {}
        setResult(null);
        setStep("ready");
      }, 2000);
    } catch (err) {
      setSaving(false);
      setError("Error al registrar fitxatge.");
      setStep("error");
    }
  };

  // ── NFC Scan ─────────────────────────────────────────────────────────────────
  if (step === "nfc") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-10 text-center max-w-sm w-full space-y-6">
          <div className="w-20 h-20 bg-[#7B2D3F]/10 rounded-full flex items-center justify-center mx-auto">
            <Wifi className="w-10 h-10 text-[#7B2D3F]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">Escaneja la tarja NFC</p>
            <p className="text-sm text-gray-500 mt-2">Apropa la tarja al teu telèfon</p>
          </div>
          <div className="border-t pt-4 text-xs text-gray-400">
            Esperant escanneig...
          </div>
        </div>
      </div>
    );
  }

  // ── PIN Authentication ───────────────────────────────────────────────────────
  if (step === "pin_auth") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-10 text-center max-w-sm w-full space-y-6">
          <div className="w-16 h-16 bg-[#7B2D3F]/10 rounded-full flex items-center justify-center mx-auto">
            <ShieldCheck className="w-8 h-8 text-[#7B2D3F]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">Verifica la teva identitat</p>
            <p className="text-sm text-gray-500 mt-3">{venue?.name}</p>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">Codi personal (4 dígits)</label>
            <input
              type="password"
              inputMode="numeric"
              maxLength="4"
              placeholder="••••"
              value={pin}
              onChange={(e) => { setPin(e.target.value.slice(0, 4)); setPinError(""); }}
              onKeyPress={(e) => e.key === 'Enter' && pin.length === 4 && handlePinSubmit()}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-center text-2xl font-bold tracking-widest focus:border-[#7B2D3F] focus:outline-none"
              autoFocus
            />
            {pinError && <p className="text-red-600 text-sm font-medium">{pinError}</p>}
          </div>

          <button
            onClick={handlePinSubmit}
            disabled={pin.length !== 4}
            className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] disabled:opacity-50 text-white rounded-xl py-3 font-bold transition-all"
          >
            Confirmar
          </button>
        </div>
      </div>
    );
  }

  // ── Loading/Confirming ────────────────────────────────────────────────────────
  if (step === "confirming") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 bg-[#7B2D3F] rounded-2xl flex items-center justify-center mx-auto shadow-lg">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
          <p className="text-gray-500 font-medium">Registrant fitxatge...</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (step === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-sm w-full">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="font-bold text-gray-800 mb-1">Error</p>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (step === "success" && result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-emerald-100 flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-10 text-center max-w-sm w-full space-y-4">
          <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-14 h-14 text-emerald-500" />
          </div>
          <div>
            <p className="text-3xl font-bold text-gray-900">{TYPE_LABELS[result.type]}</p>
            <p className="text-gray-500 mt-1 font-medium">{user?.full_name || user?.email}</p>
            <p className="text-5xl font-mono font-bold text-[#7B2D3F] mt-3">{result.time}</p>
            <p className="text-sm text-gray-400 mt-1">{venue?.name}</p>
            {tag && (
              <p className="text-xs text-gray-300 mt-1 flex items-center justify-center gap-1">
                <Wifi className="w-3 h-3" /> {tag.name}
              </p>
            )}
          </div>
          <div className="flex items-center justify-center gap-1 pt-2">
            <ShieldCheck className="w-3 h-3 text-gray-300" />
            <p className="text-[10px] text-gray-300">Registre oficial RD-Llei 8/2019</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Ready — choose action ────────────────────────────────────────────────────
  const ACTIONS = [
    { type: "entry",       label: "ENTRAR",    icon: LogIn,       color: TYPE_COLORS.entry },
    { type: "pause_start", label: "PAUSA",     icon: PauseCircle, color: TYPE_COLORS.pause_start },
    { type: "pause_end",   label: "FI PAUSA",  icon: PlayCircle,  color: TYPE_COLORS.pause_end },
    { type: "exit",        label: "SORTIR",    icon: LogOut,      color: TYPE_COLORS.exit },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex flex-col items-center justify-between p-6">
      {/* Header */}
      <div className="w-full max-w-sm text-center pt-4 space-y-1">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7B2D3F] to-[#9B4D5F] flex items-center justify-center mx-auto mb-3 shadow-md">
          <span className="text-white font-extrabold text-xs">AEH</span>
        </div>
        <p className="text-gray-500 text-sm">{venue?.name}</p>
        {tag && (
          <p className="text-xs text-[#7B2D3F] font-medium flex items-center justify-center gap-1">
            <Wifi className="w-3 h-3" /> {tag.name}
          </p>
        )}
      </div>

      {/* Main */}
      <div className="w-full max-w-sm flex-1 flex flex-col items-center justify-center gap-6 py-8">
        {/* User + time */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 text-gray-900 text-center w-full shadow-sm">
            <p className="text-4xl font-mono font-bold tabular-nums text-gray-900">{now.format("HH:mm")}</p>
            <p className="text-gray-500 text-sm capitalize mt-1">{now.format("dddd, D MMMM")}</p>
            <p className="text-gray-800 font-bold text-lg mt-3">{user?.full_name || user?.email}</p>
          </div>

        {/* Suggested action — large primary button */}
        <div className="w-full space-y-3">
          <p className="text-xs text-gray-400 text-center font-medium uppercase tracking-wide">Acció suggerida</p>
          {(() => {
            const a = ACTIONS.find(x => x.type === suggestedAction) || ACTIONS[0];
            const Icon = a.icon;
            return (
              <button
                onClick={() => handleAction(a.type)}
                disabled={saving}
                className={`${a.color} text-white w-full rounded-2xl py-6 flex flex-col items-center gap-2 shadow-xl text-xl font-bold transition-all active:scale-95 disabled:opacity-60`}
              >
                <Icon className="w-12 h-12" />
                {a.label}
              </button>
            );
          })()}
        </div>

        {/* Other actions — secondary small buttons */}
        <div className="w-full">
          <p className="text-xs text-gray-400 text-center font-medium uppercase tracking-wide mb-2">Altres accions</p>
          <div className="grid grid-cols-3 gap-2">
            {ACTIONS.filter(a => a.type !== suggestedAction).map(({ type, label, icon: Icon, color }) => (
              <button
                key={type}
                onClick={() => handleAction(type)}
                disabled={saving}
                className={`${color} text-white rounded-xl py-4 flex flex-col items-center gap-1.5 text-xs font-bold transition-all active:scale-95 disabled:opacity-60 shadow-sm`}
              >
                <Icon className="w-6 h-6" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Today's records */}
         {todayRecords.length > 0 && (
           <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
             <p className="text-xs font-semibold text-gray-500 px-4 pt-3 pb-1">Registres d'avui</p>
             <div className="divide-y divide-gray-50 max-h-48 overflow-y-auto">
               {todayRecords.map(r => {
                 const Icon = TYPE_ICONS[r.type] || LogIn;
                 return (
                   <div key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                     <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                     <span className="text-sm text-gray-600 flex-1">{TYPE_LABELS[r.type]}</span>
                     <span className="text-sm font-mono font-bold text-gray-700">{moment(r.timestamp).format("HH:mm")}</span>
                   </div>
                 );
               })}
             </div>
           </div>
         )}

         {/* Full record history */}
         {allRecords.length > 0 && (
           <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
             <p className="text-xs font-semibold text-gray-500 px-4 pt-3 pb-1">Historial complet</p>
             <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
               {allRecords.map(r => {
                 const Icon = TYPE_ICONS[r.type] || LogIn;
                 return (
                   <div key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                     <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                     <div className="flex-1 min-w-0">
                       <span className="text-sm text-gray-600">{TYPE_LABELS[r.type]}</span>
                       <p className="text-xs text-gray-400">{moment(r.date).format("DD/MM/YYYY")}</p>
                     </div>
                     <span className="text-sm font-mono font-bold text-gray-700">{moment(r.timestamp).format("HH:mm")}</span>
                   </div>
                 );
               })}
             </div>
           </div>
         )}
      </div>

      <p className="text-[10px] text-gray-300 pb-2">Kitcha · NFC · RD-Llei 8/2019</p>
    </div>
  );
}